/**
 * 演出イベント(PlayEffect)→ 音の対応づけ。
 *
 * 「いつ鳴らす/止めるか」だけをここに集約し、実際の発音は SoundService に任せる
 * (packages/engine は描画・音声非依存のまま。副作用は apps/web 側だけ)。
 *
 * 単語音声(英検単語のTTS)のルール:
 *   - 出題(単語が画面に出た)ときに1回鳴らす … **英語モードのみ**
 *   - その単語を打ち終わったときにもう1回鳴らす … **日本語 と 英語かんたん のみ**
 *     (英語 ふつう/むずかしい は聞き取って打つ練習なので、答え合わせの再生はしない)
 *   - 単語音声は常に1本だけ。重なりは mode で解決する:
 *       * 打ち終わり = `interrupt`(即座に鳴らす。保留も捨てる)
 *       * 出題       = `queue`  (鳴っていれば終わるのを待つ)
 *
 * `queue` なのは、engine が wordCompleted の直後に**同期で**次の wordStart を出すため
 * (packages/engine の completeWord)。ここで割り込むと打ち終わりの音声が鳴らずに消える。
 * 逆に、速く打って前の打ち終わり音声の途中で次を打ち終えた場合は、新しい打ち終わり音声が
 * 割り込み、保留していた出題音声は捨てられる。
 *
 * 日本語モードは従来どおり「打ち終わりのみ」で挙動を変えない。
 * 効果音は WebAudio 合成なので、単語音声(<audio>)とは干渉しない。
 */

import { getDifficultySettings, type Difficulty } from '@paltyping/engine';
import type { Language } from '../services/content';
import type { SoundName, VoicePlayMode } from '../services/sound';
import type { PlayEffect } from './playController';

/** SoundService のうち、この対応づけが使う部分だけ。 */
export interface SoundPort {
  play(name: SoundName): void;
  playVoice(url: string | null, mode?: VoicePlayMode): void;
  stopVoice(): void;
}

/** 出題時に単語音声を鳴らすモードか(英語モードのみ)。 */
export function shouldPlayVoiceOnWordStart(language: Language): boolean {
  return language === 'english';
}

/**
 * 打ち終わりに単語音声を鳴らすか。
 *
 * 英語で綴りを伏せている(= ふつう/むずかしい)ときは鳴らさない。
 * 出題時に1回だけ聞かせて、聞き取りで打たせるため(docs/02 §2)。
 */
export function shouldPlayVoiceOnWordComplete(
  language: Language,
  difficulty: Difficulty = 'easy',
): boolean {
  if (language !== 'english') return true;
  return getDifficultySettings(difficulty).showEnglishWord;
}

/** PlayEffect を受け取って鳴らすハンドラを作る。 */
export function createEffectSoundPlayer(
  sound: SoundPort,
  language: Language,
  difficulty: Difficulty = 'easy',
): (effect: PlayEffect) => void {
  const voiceOnComplete = shouldPlayVoiceOnWordComplete(language, difficulty);
  return (effect) => {
    switch (effect.type) {
      case 'correct':
        sound.play('correct');
        break;
      case 'miss':
        sound.play('miss');
        break;
      case 'wordStarted':
        // 直前の打ち終わり音声が鳴っていれば、それが終わってから鳴らす(割り込まない)。
        if (shouldPlayVoiceOnWordStart(language)) sound.playVoice(effect.word.audioUrl, 'queue');
        break;
      case 'wordCompleted':
        sound.play('wordComplete');
        // 出題音声がまだ鳴っていても、打ち終わりの音声を最初から鳴らす。
        if (voiceOnComplete) sound.playVoice(effect.word.audioUrl, 'interrupt');
        break;
      case 'fever':
        if (effect.active) sound.play('fever');
        break;
      default:
        break;
    }
  };
}
